// Tenant self-service automation schedules (Settings → Automation). A tenant
// admin can override the timing of curated platform automations for their own
// company, or fall back to the platform default.
//
// TWO WAYS IN, DIFFERENT VIEWS. integration:manage sees every curated
// automation. expenses:imprest:configure — which HR holds — sees ONLY the two
// imprest rows, because HR owns the imprest retirement cycle and nothing else
// on this page. The filter is applied to the rendered list rather than the fetch
// so there is one obvious place to read it; the scheduler still enforces the
// catalog and tenant scoping server-side, and writing any row here is gated by
// the gateway's tenant-admin check regardless of what this component shows.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Clock, Loader2, Play, RotateCcw, Pencil } from 'lucide-react';
import { getApiErrorMessage } from '@/lib/apiError';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Switch } from '@/features/shared/components/ui/switch';
import {
  listTenantJobs, putTenantJob, deleteTenantJob, runTenantJob,
  type TenantJobView, type ScheduledJob,
} from '@/services/schedulerService';
import {
  jobToForm, formToSchedule, describeJob, DOW_OPTIONS,
  type ScheduleForm, type Frequency,
} from '@/lib/cron';
import type { TFunction } from 'i18next';
import { useHasPermission } from '@/rbac/useRbac';
import { getCurrentTenant } from '@/lib/hrApi';

const TIMEZONES = ['Africa/Dar_es_Salaam', 'Africa/Nairobi', 'Africa/Kampala', 'Africa/Kigali', 'Africa/Lagos', 'Africa/Johannesburg', 'UTC'];

const previewSentence = (form: ScheduleForm, tz: string, t: TFunction): string => {
  const s = formToSchedule(form);
  if (s.schedule_type === 'cron' && !s.cron_expression) return t('enter_cron');
  return describeJob({ schedule_type: s.schedule_type, cron_expression: s.cron_expression, interval_seconds: s.interval_seconds, timezone: tz });
};

/** Catalog label/description come from the server in English; map the known job
 *  keys to translations, falling back to the server-provided text. */
const jobLabel = (t: TFunction, view: TenantJobView) => t(`job_${view.job_key}`, view.label);
const jobDesc = (t: TFunction, view: TenantJobView) => t(`job_${view.job_key}_desc`, view.description);

const inputCls = 'rounded-md border border-border bg-background px-2.5 py-1.5 text-sm';

/** Inline editor: friendly schedule builder for one automation. */
const ScheduleEditor: React.FC<{
  initial: ScheduledJob | null;
  fallbackTz: string;
  onSave: (form: ScheduleForm, tz: string) => Promise<void>;
  onCancel: () => void;
}> = ({ initial, fallbackTz, onSave, onCancel }) => {
  const { t } = useTranslation('automation');
  const [form, setForm] = useState<ScheduleForm>(
    initial ? jobToForm(initial) : { frequency: 'daily', time: '09:00', daysOfWeek: [1, 2, 3, 4, 5], dayOfMonth: 1, intervalValue: 30, intervalUnit: 'minutes', cron: '' },
  );
  const [tz, setTz] = useState(initial?.timezone || fallbackTz || 'Africa/Dar_es_Salaam');
  const [saving, setSaving] = useState(false);
  const setF = (patch: Partial<ScheduleForm>) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    const s = formToSchedule(form);
    if (s.schedule_type === 'cron' && !s.cron_expression) {
      toast.error(t('toast_incomplete'));
      return;
    }
    setSaving(true);
    try {
      await onSave(form, tz);
    } finally {
      setSaving(false);
    }
  };

  const FREQS: Frequency[] = ['daily', 'weekly', 'monthly', 'interval', 'advanced'];

  return (
    <div className="mt-3 rounded-lg border border-border bg-muted/40 p-4 space-y-3">
      <div className="flex flex-wrap gap-1.5">
        {FREQS.map((fr) => (
          <button
            key={fr}
            type="button"
            onClick={() => setF({ frequency: fr })}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              form.frequency === fr ? 'bg-orange-500 text-white' : 'bg-background border border-border text-foreground hover:bg-muted'
            }`}
          >
            {t(`freq_${fr}`)}
          </button>
        ))}
      </div>

      {(form.frequency === 'daily' || form.frequency === 'weekly' || form.frequency === 'monthly') && (
        <label className="flex items-center gap-2 text-sm">
          <span className="w-24 text-muted-foreground">{t('time_of_day')}</span>
          <input type="time" step={300} value={form.time} onChange={(e) => setF({ time: e.target.value })} className={inputCls} />
        </label>
      )}

      {form.frequency === 'weekly' && (
        <div className="flex flex-wrap gap-1.5">
          {DOW_OPTIONS.map((d) => {
            const on = form.daysOfWeek.includes(d.value);
            return (
              <button
                key={d.value}
                type="button"
                onClick={() => setF({ daysOfWeek: on ? form.daysOfWeek.filter((x) => x !== d.value) : [...form.daysOfWeek, d.value] })}
                className={`px-2.5 py-1 rounded-md text-xs ${on ? 'bg-orange-500 text-white' : 'bg-background border border-border'}`}
              >
                {d.label}
              </button>
            );
          })}
        </div>
      )}

      {form.frequency === 'monthly' && (
        <label className="flex items-center gap-2 text-sm">
          <span className="w-24 text-muted-foreground">{t('day_of_month')}</span>
          <input type="number" min={1} max={28} value={form.dayOfMonth} onChange={(e) => setF({ dayOfMonth: Number(e.target.value) || 1 })} className={`${inputCls} w-20`} />
          <span className="text-xs text-muted-foreground">{t('day_of_month_hint')}</span>
        </label>
      )}

      {form.frequency === 'interval' && (
        <label className="flex items-center gap-2 text-sm">
          <span className="w-24 text-muted-foreground">{t('run_every')}</span>
          <input type="number" min={1} value={form.intervalValue} onChange={(e) => setF({ intervalValue: Number(e.target.value) || 1 })} className={`${inputCls} w-20`} />
          <select value={form.intervalUnit} onChange={(e) => setF({ intervalUnit: e.target.value as 'minutes' | 'hours' })} className={inputCls}>
            <option value="minutes">{t('minutes')}</option>
            <option value="hours">{t('hours')}</option>
          </select>
        </label>
      )}

      {form.frequency === 'advanced' && (
        <label className="flex items-center gap-2 text-sm">
          <span className="w-24 text-muted-foreground">{t('cron')}</span>
          <input value={form.cron} onChange={(e) => setF({ cron: e.target.value })} placeholder="0 9 * * *" className={`${inputCls} font-mono flex-1`} />
        </label>
      )}

      {form.frequency !== 'interval' && (
        <label className="flex items-center gap-2 text-sm">
          <span className="w-24 text-muted-foreground">{t('timezone')}</span>
          <select value={tz} onChange={(e) => setTz(e.target.value)} className={inputCls}>
            {TIMEZONES.map((tzv) => <option key={tzv} value={tzv}>{tzv}</option>)}
          </select>
        </label>
      )}

      <div className="text-xs rounded-md bg-orange-50 dark:bg-orange-500/10 text-orange-800 dark:text-orange-300 px-3 py-2">
        <b>{t('preview')}</b> {previewSentence(form, tz, t)}
      </div>

      <div className="flex gap-2">
        <Button size="sm" onClick={save} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('save_schedule')}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} disabled={saving}>{t('cancel')}</Button>
      </div>
    </div>
  );
};

const AutomationCard: React.FC<{ view: TenantJobView; onChanged: () => void; orgTimezone?: string }> = ({ view, onChanged, orgTimezone }) => {
  const { t } = useTranslation('automation');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const effective = view.override ?? view.default;
  const isCustom = !!view.override;

  const save = async (form: ScheduleForm, tz: string) => {
    const s = formToSchedule(form);
    try {
      await putTenantJob(view.job_key, { ...s, timezone: tz, enabled: view.override?.enabled ?? true });
      toast.success(t('toast_saved'));
      setEditing(false);
      onChanged();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, t('toast_save_failed')));
    }
  };

  const toggleEnabled = async (on: boolean) => {
    if (!view.override) return;
    setBusy(true);
    const o = view.override;
    try {
      await putTenantJob(view.job_key, {
        schedule_type: o.schedule_type, cron_expression: o.cron_expression,
        interval_seconds: o.interval_seconds, timezone: o.timezone, enabled: on,
      });
      onChanged();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, t('toast_save_failed')));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    try {
      await deleteTenantJob(view.job_key);
      toast.success(t('toast_reverted'));
      onChanged();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, t('toast_reset_failed')));
    } finally {
      setBusy(false);
    }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const run = await runTenantJob(view.job_key);
      if (run?.status === 'published') toast.success(t('toast_triggered'));
      else toast(t('toast_trigger_queued', { status: run?.status ?? 'queued' }));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, t('toast_trigger_failed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-border p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-semibold text-foreground">{jobLabel(t, view)}</div>
          <div className="text-sm text-muted-foreground">{jobDesc(t, view)}</div>
          <div className="mt-2 text-sm">
            {effective ? (
              <span>
                <span className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-medium mr-2 ${isCustom ? 'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300' : 'bg-muted text-muted-foreground'}`}>
                  {isCustom ? t('badge_custom') : t('badge_default')}
                </span>
                {describeJob(effective)}
                {isCustom && !view.override?.enabled && <span className="ml-2 text-xs text-muted-foreground">{t('paused')}</span>}
              </span>
            ) : (
              <span className="text-muted-foreground">{t('not_scheduled')}</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {isCustom && (
            <Switch checked={!!view.override?.enabled} disabled={busy} onCheckedChange={toggleEnabled} />
          )}
          <Button size="sm" variant="outline" onClick={() => setEditing((v) => !v)} disabled={busy}>
            <Pencil className="w-3.5 h-3.5 mr-1" /> {isCustom ? t('edit') : t('customize')}
          </Button>
          {isCustom && (
            <>
              <Button size="sm" variant="ghost" onClick={runNow} disabled={busy} title={t('run_now')}>
                <Play className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="ghost" onClick={reset} disabled={busy} title={t('reset_default')}>
                <RotateCcw className="w-3.5 h-3.5" />
              </Button>
            </>
          )}
        </div>
      </div>

      {editing && (
        <ScheduleEditor
          initial={view.override}
          // The org timezone is the tenant's answer to "when is 06:30 here", and a
          // scheduled job is the one case with no viewer to ask — so it belongs
          // here rather than a hardcoded Dar es Salaam, which silently ran every
          // other tenant's jobs on Tanzanian time. The job's own default still
          // wins where it has one; the literal stays as the last resort for a
          // tenant that has never set one.
          fallbackTz={view.default?.timezone || orgTimezone || 'Africa/Dar_es_Salaam'}
          onSave={save}
          onCancel={() => setEditing(false)}
        />
      )}
    </div>
  );
};

// IMPREST_JOB_KEYS mirrors scheduler-service/internal/catalog/catalog.go. Two
// keys, not a prefix match, so a future imprest_* job does not become visible to
// HR by accident just because of how it was named.
const IMPREST_JOB_KEYS = ['imprest_policy_reminder_monthly', 'imprest_outstanding_snapshot_monthly'];

export const AutomationSettingsPage: React.FC = () => {
  const { t } = useTranslation('automation');
  const [jobs, setJobs] = useState<TenantJobView[]>([]);
  const [loading, setLoading] = useState(true);
  // The tenant's configured timezone (Company settings → General). It was stored
  // and displayed but NOTHING read it, so every tenant's scheduled jobs fell back
  // to a hardcoded Africa/Dar_es_Salaam. Fetched once here rather than per card.
  // Best-effort: a failure leaves the previous literal fallback in place, which
  // is no worse than before and must not block the page.
  const [orgTimezone, setOrgTimezone] = useState<string | undefined>();
  const canAll = useHasPermission('integration:manage');
  const canImprest = useHasPermission('expenses:imprest:configure');
  // A full admin sees everything; otherwise only what this permission covers.
  const visible = canAll ? jobs : jobs.filter((j) => IMPREST_JOB_KEYS.includes(j.job_key));

  const load = () => {
    setLoading(true);
    listTenantJobs()
      .then(setJobs)
      .catch((e) => toast.error(getApiErrorMessage(e, t('toast_load_failed'))))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    getCurrentTenant()
      .then((tn: any) => setOrgTimezone(tn?.timezone || undefined))
      .catch(() => undefined);
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Clock className="w-6 h-6 text-orange-500" /> {t('title')}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {canAll ? t('subtitle') : t('subtitle_imprest_only', {
            defaultValue: 'The imprest retirement automations for your company. Other schedules are managed by an administrator.',
          })}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('card_title')}</CardTitle>
          <CardDescription>{t('card_desc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> {t('loading')}
            </div>
          ) : visible.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">{t('none')}</div>
          ) : (
            visible.map((j) => <AutomationCard key={j.job_key} view={j} onChanged={load} orgTimezone={orgTimezone} />)
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AutomationSettingsPage;
